import { t } from "i18next";

import {
  Api,
  ApiException,
  createErrorResult,
  createSuccessResult,
  errors,
  getHelpTopicAssetsBasePath,
  getHelpTopicContentPath,
  getHelpTopicsManifestPath,
  Log,
  type HelpTopicContentLocale,
  type IHelpTopicContentDto,
  type IHelpTopicManifest,
  type SimpleResult,
} from "@ryde/shared";

// The manifest is the same for every company and every session, one fetch per page load is enough.
const manifestCache: { data?: IHelpTopicManifest } = {};

export const fetchHelpTopicManifest = async (
  companyId: number,
): Promise<SimpleResult<IHelpTopicManifest>> => {
  if (manifestCache.data) {
    return createSuccessResult(manifestCache.data);
  }

  try {
    const response = await Api.get<IHelpTopicManifest>(
      getHelpTopicsManifestPath(companyId),
    );
    if (!response?.data) {
      return createErrorResult(errors.common.unknownError);
    }

    manifestCache.data = response.data;
    return createSuccessResult(response.data);
  } catch (error: unknown) {
    Log.errorAndToast(t(errors.common.unknownError), error);
    return createErrorResult(errors.common.unknownError);
  }
};

export const fetchHelpTopicContent = async ({
  companyId,
  helpTopikUrl,
  locale,
}: {
  companyId: number;
  helpTopikUrl: string;
  locale: HelpTopicContentLocale;
}): Promise<SimpleResult<IHelpTopicContentDto>> => {
  try {
    const response = await Api.get<IHelpTopicContentDto>(
      getHelpTopicContentPath(companyId),
      { path: helpTopikUrl, locale },
    );
    if (!response?.data) {
      return createErrorResult(errors.helpTopics.topicNotFound);
    }

    return createSuccessResult(response.data);
  } catch (error: unknown) {
    const translationKey =
      error instanceof ApiException && error.translationKey
        ? error.translationKey
        : errors.helpTopics.topicNotFound;
    return createErrorResult(translationKey);
  }
};

/** Absolute URL the topic's relative image links resolve against; cookies ride along because the API shares the site. */
export const buildHelpTopicAssetsBaseUrl = ({
  companyId,
  content,
}: {
  companyId: number;
  content: IHelpTopicContentDto;
}): string => {
  const assetsPath = getHelpTopicAssetsBasePath({
    companyId,
    locale: content.locale,
    topicPath: content.path,
  });
  return `${Api.baseUrl ?? ""}${assetsPath}`;
};
